import React, { useEffect, useState } from "react";
import { Button, Card, CardHeader, Input, Label } from "reactstrap";
import { useDispatch } from "react-redux";
import { Table, TableRow } from "../../../components/Table";
import { TRA_FILTER } from "../../../constant";
import { useRole } from "../../../redux/slices/authSlice";
import {
  get_Manual_Merchant_Entries,
  useManualMerchantEntries,
  useManualMerchantEntriesLoading,
} from "../../../redux/slices/merchantSlice";
import AddManualPayoutModal from "../../../components/Custom/Modals/AddManualPayoutModal/AddManualPayoutModal";
import AddManualPayin from "../../../components/Custom/Modals/AddManualPayin/AddManualPayin";
import { seperator } from "../../../helperFunctions";

// MAIN menu "Manual Merchant Entry": add manual deposits / withdrawals for any merchant (same modals that used to
// sit on the merchant page) and list what has been entered (tbl_transaction_manual + manual rows of tbl_payout).
const ManualMerchantEntry = () => {
  const dispatch = useDispatch();
  const role = useRole("Manual Merchant Entry");
  const entries = useManualMerchantEntries();
  const isLoading = useManualMerchantEntriesLoading();

  const [filter, setFilter] = useState({ ...TRA_FILTER, merchant_id: "", entry_type: "ALL" });
  const [filterColumns, setFilterColumns] = useState([]);
  const [addManualPayoutModal, setAddManualPayoutModal] = useState(false);
  const [addManualPayinModal, setAddManualPayinModal] = useState(false);

  const canAdd = role?.includes("add-manual-merchant-entry");
  const merchants = entries?.merchants || [];

  const columns = [
    { title: "DATE", name: "created_at_ist" },
    {
      title: "TYPE",
      name: "entry_type",
      Cell: (data) => (
        <span className={`badge ${data?.entry_type === "DEPOSIT" ? "bg-success" : "bg-warning"}`}>
          {data?.entry_type}
        </span>
      ),
    },
    {
      title: "MERCHANT",
      name: "merchant_name",
      Cell: (data) => (
        <div className="cell-order">
          <div className="order-singal-div"><p>{data?.merchant_name || ""}</p></div>
          <div className="order-singal-div"><p>{data?.merchant_id || ""}</p></div>
        </div>
      ),
    },
    { title: "ENTRY ID", name: "id" },
    { title: "AMOUNT", name: "amount", Cell: (data) => <>{seperator(data?.amount)}</> },
    { title: "FEES", name: "fees", Cell: (data) => <>{seperator(data?.fees)}</> },
    { title: "NET", name: "net_amount", Cell: (data) => <>{seperator(data?.net_amount)}</> },
    { title: "UTR / REF", name: "utr" },
    { title: "STATUS", name: "status" },
    {
      title: "BANK",
      name: "bank_holder",
      Cell: (data) =>
        data?.entry_type === "WITHDRAWAL" ? (
          <div className="cell-order">
            <div className="order-singal-div"><p>{data?.bank_holder || ""}</p></div>
            <div className="order-singal-div"><p>{data?.account_number || ""}</p></div>
            <div className="order-singal-div"><p>{data?.ifsc_code || ""}</p></div>
          </div>
        ) : (
          <>-</>
        ),
    },
    { title: "REMARK", name: "remark" },
  ];

  useEffect(() => {
    setFilterColumns(columns.map((item) => item.name));
  }, []);

  const fetchEntries = async () => {
    try {
      const { page_no, limit, merchant_id, entry_type } = filter;
      await dispatch(
        get_Manual_Merchant_Entries({
          page_no,
          limit,
          filter_data: { merchant_id: merchant_id || null, entry_type },
        })
      ).unwrap();
    } catch (err) {}
  };

  useEffect(() => {
    if (role?.includes("manualMerchantEntry-list")) {
      fetchEntries();
    }
  }, [filter]);

  if (!role?.includes("manualMerchantEntry-list")) {
    return null;
  }

  const selectedMerchant = filter.merchant_id;

  return (
    <>
      <Card>
        <CardHeader className="flex-column align-items-start">
          <div className="d-flex align-items-center w-100 justify-content-between flex-wrap gap-1">
            <h4 className="main-title">
              <b>Manual Merchant Entry</b>
            </h4>
            {canAdd ? (
              <div className="d-flex gap-1 flex-wrap">
                <Button
                  color="primary"
                  disabled={!selectedMerchant}
                  title={selectedMerchant ? "" : "Select a merchant first"}
                  onClick={() => setAddManualPayoutModal(true)}
                >
                  Add Manual Withdrawal
                </Button>
                <Button
                  color="primary"
                  disabled={!selectedMerchant}
                  title={selectedMerchant ? "" : "Select a merchant first"}
                  onClick={() => setAddManualPayinModal(true)}
                >
                  Add Manual Deposit
                </Button>
              </div>
            ) : null}
          </div>
          <div className="d-flex my-xl-auto right-content align-items-end button-space flex-wrap gap-1 mt-1">
            <div>
              <Label>Merchant</Label>
              <Input
                type="select"
                value={filter.merchant_id}
                onChange={(e) => setFilter({ ...filter, page_no: 1, merchant_id: e.target.value })}
              >
                <option value="">All merchants</option>
                {merchants.map((m) => (
                  <option key={m.merchant_id} value={m.merchant_id}>
                    {m.merchant_name} ({m.merchant_id})
                  </option>
                ))}
              </Input>
            </div>
            <div>
              <Label>Type</Label>
              <Input
                type="select"
                value={filter.entry_type}
                onChange={(e) => setFilter({ ...filter, page_no: 1, entry_type: e.target.value })}
              >
                <option value="ALL">Deposit + Withdrawal</option>
                <option value="DEPOSIT">Deposit</option>
                <option value="WITHDRAWAL">Withdrawal</option>
              </Input>
            </div>
            <Button color="btn btn-outline-secondary" onClick={() => setFilter({ ...filter })}>
              Refresh
            </Button>
          </div>
        </CardHeader>
        <Table
          columns={columns}
          isLoading={isLoading}
          data={entries?.data || []}
          isData={!!entries?.data?.length}
          filterColumns={filterColumns}
          count={entries?.total_item || 0}
          pagination={filter}
          isExpandable={false}
          handlePaginationChange={(pagination) => setFilter({ ...filter, ...pagination })}
          onColumnsChange={(cols) => setFilterColumns(cols)}
        >
          {entries?.data?.map((item) => (
            <TableRow
              key={`${item.entry_type}-${item.id}`}
              columns={columns}
              item={item}
              filterColumns={filterColumns}
              isExpandable={false}
            />
          ))}
        </Table>
      </Card>

      <AddManualPayoutModal
        addManualPayoutModal={addManualPayoutModal}
        onCloseModal={() => {
          setAddManualPayoutModal(false);
          setFilter({ ...filter });
        }}
        id={selectedMerchant}
      />
      <AddManualPayin
        addManualPayinModal={addManualPayinModal}
        onCloseAddManualPayinModal={() => {
          setAddManualPayinModal(false);
          setFilter({ ...filter });
        }}
        id={selectedMerchant}
      />
    </>
  );
};

export default ManualMerchantEntry;
